import { computeOffset, tapDeltaMs, type CalibrationResult } from '../core/calibration';
import type { Clock, Millis, Seconds } from '../core/types';

export const CLICK_PERIOD: Seconds = 0.5;
export const CLICK_COUNT = 16;
export const CLICK_LEAD_IN: Seconds = 1.2;
const CLICK_TAIL: Seconds = 0.35;
const ACCENT_EVERY = 4;

export interface CalibrationDeps {
  context: BaseAudioContext;
  clock: Clock;
  host: HTMLElement;
}

function click(context: BaseAudioContext, at: Seconds, accent: boolean): OscillatorNode {
  const osc = context.createOscillator();
  const gain = context.createGain();
  osc.type = 'square';
  osc.frequency.setValueAtTime(accent ? 1320 : 880, at);
  gain.gain.setValueAtTime(0.0001, at);
  gain.gain.exponentialRampToValueAtTime(accent ? 0.3 : 0.18, at + 0.004);
  gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.05);
  osc.connect(gain).connect(context.destination);
  osc.start(at);
  osc.stop(at + 0.08);
  return osc;
}

function overlay(): { root: HTMLElement; dots: HTMLElement; read: HTMLElement } {
  const root = document.createElement('div');
  root.id = 'calib';
  root.innerHTML = [
    '<h2>CALIBRAR</h2>',
    '<p>bate junto com o click — espaço ou toque na tela</p>',
    '<div id="calib-dots"></div>',
    '<div id="calib-read">…</div>',
    '<small>esc cancela</small>',
  ].join('');
  const dots = root.querySelector<HTMLElement>('#calib-dots');
  const read = root.querySelector<HTMLElement>('#calib-read');
  if (!dots || !read) throw new Error('calibration overlay is broken');
  return { root, dots, read };
}

export function runCalibration(deps: CalibrationDeps): Promise<CalibrationResult | null> {
  const { context, clock, host } = deps;
  const ui = overlay();
  host.appendChild(ui.root);

  const gridStart = context.currentTime + CLICK_LEAD_IN;
  const lastClick = gridStart + CLICK_PERIOD * (CLICK_COUNT - 1);
  const voices: OscillatorNode[] = [];
  for (let i = 0; i < CLICK_COUNT; i++) {
    voices.push(click(context, gridStart + CLICK_PERIOD * i, i % ACCENT_EVERY === 0));
  }

  const hush = (): void => {
    const at = context.currentTime;
    for (const voice of voices) {
      try {
        voice.stop(at);
      } catch {
        continue;
      }
    }
    voices.length = 0;
  };

  const samples: Millis[] = [];
  const paint = (): void => {
    const elapsed = Math.max(0, context.currentTime - gridStart);
    const heard = Math.min(CLICK_COUNT, Math.floor(elapsed / CLICK_PERIOD) + 1);
    ui.dots.innerHTML = Array.from({ length: CLICK_COUNT }, (_, i) => {
      const state = i < samples.length ? ' tapped' : i < heard ? ' past' : '';
      return `<i class="dot${state}"></i>`;
    }).join('');
  };

  return new Promise<CalibrationResult | null>((resolve) => {
    let done = false;
    let frame = 0;

    const finish = (result: CalibrationResult | null): void => {
      if (done) return;
      done = true;
      hush();
      cancelAnimationFrame(frame);
      removeEventListener('keydown', onKeyDown, true);
      removeEventListener('keyup', swallow, true);
      removeEventListener('pointerdown', onPointerDown, true);
      removeEventListener('pointerup', swallow, true);
      ui.root.remove();
      resolve(result);
    };

    const tap = (at: Seconds): void => {
      if (at < gridStart - CLICK_PERIOD / 2 || samples.length >= CLICK_COUNT) return;
      const delta = tapDeltaMs(at, gridStart, CLICK_PERIOD);
      samples.push(delta);
      const sign = delta >= 0 ? '+' : '';
      ui.read.textContent = `${samples.length}/${CLICK_COUNT}  ·  ${sign}${delta.toFixed(0)} ms`;
      paint();
    };

    function swallow(event: Event): void {
      event.stopPropagation();
    }

    function onKeyDown(event: KeyboardEvent): void {
      if (event.code === 'Escape') {
        finish(null);
        return;
      }
      if (event.code !== 'Space' && event.code !== 'ArrowUp') return;
      event.preventDefault();
      event.stopPropagation();
      if (event.repeat) return;
      tap(clock.atEvent(event));
    }

    function onPointerDown(event: PointerEvent): void {
      event.stopPropagation();
      tap(clock.atEvent(event));
    }

    addEventListener('keydown', onKeyDown, true);
    addEventListener('keyup', swallow, true);
    addEventListener('pointerdown', onPointerDown, true);
    addEventListener('pointerup', swallow, true);

    const loop = (): void => {
      paint();
      if (context.currentTime > lastClick + CLICK_TAIL) {
        finish(computeOffset(samples));
        return;
      }
      frame = requestAnimationFrame(loop);
    };
    frame = requestAnimationFrame(loop);
  });
}
